import React, { Component } from 'react';
import * as THREE from 'three';

/**
 * SparksLayer — 3D-слой «искры летят вверх» за прямоугольником (тестовый полигон /stats).
 *
 * Подход:
 *  - THREE.Points + кастомный ShaderMaterial (положение/жизнь/мерцание считаются в шейдере);
 *  - каждая искра — спрайт: голова + шлейф-хвост вниз;
 *  - NormalBlending + premultiplied-alpha (цвет * alpha): выглядит одинаково на белом и тёмном фоне;
 *  - зона рисования задаётся пропсами width/height (CSS-px) и привязана к прямоугольнику,
 *    за которым «искрит»: rectWidth/rectHeight/marginBottom — откуда взлетают искры.
 */

// сколько искр одновременно в зоне
const SPARK_COUNT = 320;

const vertexShader = `
    precision highp float;

    // упакованные атрибуты (лимит на число атрибутов шейдера!):
    // aColor4 = rgb + seed; aPath = startX/startY/endX/travel;
    // aCycle = life/phase/burn/size; aShape = head/tail/sway/wob
    attribute vec4 aColor4;
    attribute vec4 aPath;
    attribute vec4 aCycle;
    attribute vec4 aShape;

    uniform float uTime;
    uniform float uPixelRatio;

    varying float vAge;
    varying float vAlpha;
    varying float vSize;
    varying float vHead;
    varying float vTail;
    varying vec3  vColor;

    void main() {

        float life  = aCycle.x;
        float age = mod(uTime + aCycle.y, life) / life;

        float startX = aPath.x;
        float startY = aPath.y;
        float endX   = aPath.z;
        float travel = aPath.w;

        // дуга подъёма: быстрый рывок внизу, плавное замирание к концу жизни
        float t = 1.0 - pow(1.0 - age, 1.7);

        // искра летит по прямой от старта к цели над центром:
        // боковые искры «сходятся» к центру (endX ближе к 0), центральные летят почти вертикально
        float x = startX + (endX - startX) * t;

        // лёгкое живое покачивание вокруг траектории
        x += sin(age * 6.28318 * aShape.w + aColor4.w * 60.0) * aShape.z * age;

        float y = startY + travel * t;

        vec4 mvPosition = modelViewMatrix * vec4(x, y, 0.0, 1.0);
        gl_Position = projectionMatrix * mvPosition;

        // появление (вспышка) и затухание в конце жизни
        float born = smoothstep(0.0, 0.06, age);
        float die  = 1.0 - smoothstep(aCycle.z * 0.6, aCycle.z, age);

        // мерцание: быстрое + медленное
        float flicker = 0.6 + 0.4 * sin(age * 30.0 + aColor4.w * 250.0);
        flicker *= 0.85 + 0.15 * sin(uTime * 1.4 + aColor4.w * 80.0);

        float vis = born * die;
        float alpha = clamp(vis * flicker, 0.0, 1.0);
        float scaleDown = 0.12 + 0.88 * vis; // «мёртвые» точки сжимаем (меньше fill-rate)

        vAge   = age;
        vAlpha = alpha;
        vSize  = aCycle.w * uPixelRatio * scaleDown;
        vHead  = aShape.x * uPixelRatio * scaleDown;
        vTail  = aShape.y * uPixelRatio * scaleDown;
        vColor = aColor4.xyz;

        gl_PointSize = clamp(vSize, 1.0, 300.0);
    }
`;

const fragmentShader = `
    precision highp float;

    varying float vAge;
    varying float vAlpha;
    varying float vSize;
    varying float vHead;
    varying float vTail;
    varying vec3  vColor;

    void main() {

        if (vAlpha <= 0.002) discard;

        // координаты внутри спрайта: -1..1; +y — голова (искра летит вверх)
        vec2 uv = gl_PointCoord * 2.0 - 1.0;
        float halfPx = vSize * 0.5;
        float crossPx = uv.x * halfPx;
        float alongPx = uv.y * halfPx;

        float headR = max(vHead, 1.0);

        // голова: мягкий шар + более плотное ядро
        float dHead = length(vec2(crossPx, alongPx));
        float head = exp(-(dHead * dHead) / (headR * headR));
        float core = exp(-(dHead * dHead) / (headR * headR * 0.14));

        // хвост-шлейф: ТОЛЬКО вниз от головы (сверху ничего не светится)
        float tailLen = max(vTail, 2.0);
        float down = max(0.0, -alongPx);
        float tailGlow = 0.0;
        if (alongPx < headR * 0.6) {
            float fall = exp(-down / tailLen);
            fall *= 1.0 - smoothstep(tailLen * 0.75, tailLen, down);
            float widthPx = max(headR * 0.5, 1.0);
            float lat = exp(-(crossPx * crossPx) / (widthPx * widthPx));
            tailGlow = fall * lat;
        }

        // ярко-красное «тело»; внутри — светлее, по краям НЕ уходит в чёрный
        vec3 bodyCol = vColor;                                        // палитра: красные тона
        vec3 coreCol = mix(vec3(1.0, 0.9, 0.8), bodyCol, 0.3);        // светло-красный центр
        vec3 tailCol = bodyCol * 0.7;                                 // хвост: чуть темнее, но остаётся красным

        // радиальный переход: середина светлее, к краю головы — ярко-красный
        vec3 col = mix(bodyCol, coreCol, smoothstep(0.0, 0.9, core));
        col = mix(col, tailCol, tailGlow * 0.45);

        // мягкое «остывание» к концу жизни (до чёрного не темнеем — гаснет через alpha)
        col *= 1.0 - 0.25 * smoothstep(0.3, 0.85, vAge);

        // premultiplied-alpha: цвет уже умножен на alpha => нет «серых квадратов» по краям спрайта
        float glow = clamp(head + core * 0.6 + tailGlow * 0.5, 0.0, 1.0);
        float a = clamp(vAlpha * glow, 0.0, 1.0);

        gl_FragColor = vec4(col * a, a);
    }
`;

// Палитра искры: светлый красный → ярко-красный (без ухода в чёрный)
const COLOR_STOPS = [
    [1.0, 0.0, 0.0],
    [1.0, 0.0, 0.0],
    [2.0, 0.8, 0.0],
    [1.0, 0.0, 0.0]
];

function pickColor() {
    const a = Math.random() * (COLOR_STOPS.length - 1);
    const i = Math.floor(a);
    const t = a - i;
    const c1 = COLOR_STOPS[i];
    const c2 = COLOR_STOPS[Math.min(i + 1, COLOR_STOPS.length - 1)];
    return [
        c1[0] ,//+ (c2[0] - c1[0]) * t,
        c1[1] ,//+ (c2[1] - c1[1]) * t,
        c1[2] ,//+ (c2[2] - c1[2]) * t
    ];
}

class SparksLayer extends Component {

    canvasRef = React.createRef();
    rafId = null;
    startedAt = 0;
    renderer = null;
    scene = null;
    camera = null;
    geometry = null;
    material = null;
    uniforms = null;

    componentDidMount() {
        this.init();
    }

    componentWillUnmount() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
        }
        if (this.geometry) this.geometry.dispose();
        if (this.material) this.material.dispose();
        if (this.renderer) this.renderer.dispose();
        this.renderer = null;
        this.scene = null;
        this.camera = null;
        this.geometry = null;
        this.material = null;
    }

    init = () => {
        if (this.renderer) return;

        const canvas = this.canvasRef.current;

        this.renderer = new THREE.WebGLRenderer({
            canvas,
            alpha: true,
            antialias: true
        });
        this.renderer.setClearColor(0x000000, 0);

        this.scene = new THREE.Scene();
        // ортокамера: 1 юнит мира = 1 CSS-пиксель канваса (зона маленькая и фиксированная)
        this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -500, 500);
        this.scene.add(this.camera);

        this.geometry = new THREE.BufferGeometry();

        this.uniforms = {
            uTime: { value: 0 },
            uPixelRatio: { value: 1 }
        };

        // NormalBlending с premultiplied-alpha выводом шейдера:
        // корректно ложится поверх любого фона (белый/тёмный) и не даёт «квадратов»
        this.material = new THREE.ShaderMaterial({
            depthWrite: false,
            depthTest: false,
            blending: THREE.CustomBlending,
            blendEquation: THREE.AddEquation,
            blendSrc: THREE.OneFactor,
            blendDst: THREE.OneMinusSrcAlphaFactor,
            transparent: true,
            uniforms: this.uniforms,
            vertexShader,
            fragmentShader
        });

        const points = new THREE.Points(this.geometry, this.material);
        points.frustumCulled = false;
        this.scene.add(points);

        this.applySize();
        this.startedAt = performance.now();
        this.tick();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.width !== this.props.width || prevProps.height !== this.props.height) {
            this.applySize();
        }
    }

    applySize = () => {
        if (!this.renderer || !this.camera) return;

        const { width, height } = this.props;
        if (!width || !height) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        this.renderer.setPixelRatio(dpr);
        // updateStyle = false — размер канваса задаёт CSS (100% родителя)
        this.renderer.setSize(width, height, false);

        this.camera.left = -width / 2;
        this.camera.right = width / 2;
        this.camera.top = height / 2;
        this.camera.bottom = -height / 2;
        this.camera.updateProjectionMatrix();

        this.uniforms.uPixelRatio.value = dpr;
        this.spawnSparks();
        this.startedAt = performance.now();
    }

    spawnSparks = () => {
        if (!this.geometry) return;

        const count = SPARK_COUNT;
        const {
            width,
            height,
            rectHeight,
            marginBottom
        } = this.props;

        // «мир» = CSS-пиксели канваса, центр зоны — в (0, 0)
        const halfW = width / 2;
        const halfH = height / 2;
        const bottomY = -halfH; // низ зоны
        const rectBottomY = -halfH + marginBottom;
        const rectTopY = rectBottomY + rectHeight;
        const topY = halfH; // верх зоны (над прямоугольником запас = 50% высоты)

        const position = new Float32Array(count * 3); // нужен three для отрисовки Points (сами координаты считает шейдер)

        // упакованные атрибуты (4 vec4 вместо 14 скаляров — лимит атрибутов шейдера):
        const colors4 = new Float32Array(count * 4); // rgb + seed
        const paths   = new Float32Array(count * 4); // startX / startY / endX / travel
        const cycles  = new Float32Array(count * 4); // life / phase / burn / sizeCss
        const shapes  = new Float32Array(count * 4); // head / tail / sway / wob

        // координата, от которой начинается «бок» (запас слева/справа от прямоугольника)
        const maxStartX = halfW - 6;

        for (let i = 0; i < count; i++) {
            const i4 = i * 4;

            const seed = Math.random();

            // рождаются в нижней части зоны (за прямоугольником / чуть ниже него)
            const startY = bottomY + Math.random() * (rectHeight + marginBottom);
            // цель — выше верхней грани прямоугольника (в зоне запаса над ним)
            const endY = rectTopY + (0.15 + Math.random() * 0.85) * (topY - rectTopY);

            const startX = (Math.random() * 2 - 1) * maxStartX;

            // насколько искра далеко от центра (0..1)
            const side = Math.abs(startX) / maxStartX;

            // боковые искры поднимаются ниже, центральные — выше всего
            const heightFactor = 1.0 - 0.1 * Math.pow(side, 2.4);
            // боковые искры сильнее «сходятся» к центру
            const converge = 1.45 * Math.pow(side, 3.15);
            const endX = startX * (1.0 - converge);

            const life = 1.2 + Math.pow(Math.random(), 1.4) * 1.9; // длительность жизни, сек
            const burn = 0.5 + Math.random() * 0.46;               // когда гаснет
            const travel = Math.max(8, (endY - startY) * heightFactor);

            // размеры спрайта в CSS-пикселях: голова крупнее/круглее, хвост короче
            const head = 2.8 + Math.pow(Math.random(), 2.0) * 3.0; // радиус «головы»
            const tail = head * (0.7 + Math.pow(Math.random(), 2.6) * 4.4); // длина шлейфа
            const size = Math.max(7, (head + tail) * 2);

            const color = pickColor();

            colors4[i4]     = color[0];
            colors4[i4 + 1] = color[1];
            colors4[i4 + 2] = color[2];
            colors4[i4 + 3] = seed;

            paths[i4]     = startX;
            paths[i4 + 1] = startY;
            paths[i4 + 2] = endX;
            paths[i4 + 3] = travel;

            cycles[i4]     = life;
            cycles[i4 + 1] = Math.random() * life; // случайная фаза (бесшовный цикл)
            cycles[i4 + 2] = burn;
            cycles[i4 + 3] = size;

            shapes[i4]     = head;
            shapes[i4 + 1] = tail;
            shapes[i4 + 2] = 1.5 + Math.random() * 5;   // лёгкое покачивание, px
            shapes[i4 + 3] = 1 + Math.random() * 2.5;   // сколько волн покачивания за жизнь

            position[i * 3]     = 0;
            position[i * 3 + 1] = 0;
            position[i * 3 + 2] = 0;
        }

        this.geometry.setAttribute('position', new THREE.BufferAttribute(position, 3));
        this.geometry.setAttribute('aColor4', new THREE.BufferAttribute(colors4, 4));
        this.geometry.setAttribute('aPath', new THREE.BufferAttribute(paths, 4));
        this.geometry.setAttribute('aCycle', new THREE.BufferAttribute(cycles, 4));
        this.geometry.setAttribute('aShape', new THREE.BufferAttribute(shapes, 4));
    }

    tick = () => {
        if (!this.renderer) return;
        this.uniforms.uTime.value = (performance.now() - this.startedAt) / 1000;
        this.renderer.render(this.scene, this.camera);
        this.rafId = requestAnimationFrame(this.tick);
    }

    render() {
        return <canvas
            ref={this.canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none'
            }}
        />;
    }
}

export default SparksLayer;
